/**
 * Planned maintenance windows — the calendar's view of them, and the
 * two writes that manage them.
 *
 * A WINDOW QUERY like `useScheduleOccurrences`, and for the same
 * reason: the calendar shows a range, and a window overlapping that
 * range is what matters, not one contained by it. Overnight
 * maintenance is the normal case.
 *
 * The same data serves two purposes and must not be fetched twice for
 * them: the band drawn on the grid, and the answer to "would a task
 * starting at 02:00 be refused?" that the dispatch form asks before
 * submitting. Deriving the second from the first is what keeps the form
 * and the calendar from disagreeing about which windows exist — and
 * neither is authoritative anyway. The server refuses on the command
 * path regardless of what the browser believed.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import {
  createMaintenanceWindow,
  deleteMaintenanceWindow,
  fetchMaintenanceWindows,
} from '../v1-client';
import { apiErrorCode } from './useFireMode';
import { invalidatePlanCheck } from './usePlanCheck';
import type { CreateMaintenanceWindowRequest, MaintenanceWindow, SiteId } from '../v1';

export const MAINTENANCE_KEY = 'maintenance-windows' as const;
export const maintenanceKey = (siteId: string, fromMs: number, toMs: number) =>
  [MAINTENANCE_KEY, siteId, fromMs, toMs] as const;

/** The v1 code a refused dispatch carries when a window is open. */
export const MAINTENANCE_HOLD_CODE = 'maintenance_window_active';

/**
 * A refusal that is a HOLD, not a denial — the same distinction
 * `isFireModeHold` draws, and it matters for the same reason: the
 * caller holds the capability and will hold it again when the window
 * closes. Rendering this with the generic error copy reads as "you are
 * not allowed to do this" and sends the operator to ask for a
 * permission they already have.
 */
export function isMaintenanceHold(err: unknown): boolean {
  return apiErrorCode(err) === MAINTENANCE_HOLD_CODE;
}

// "Blocked time", the calendar's name for a window (2026-10-31). Remove
// is the one change the console offers a booked window: there is no way
// to shorten one, so the copy no longer suggests it.
export const MAINTENANCE_HOLD_TITLE = 'Held by blocked time';
export const MAINTENANCE_HOLD_COPY =
  'This site is closed for maintenance, so nothing is being dispatched. ' +
  'Work already running is finishing. To dispatch now, remove the ' +
  'blocked time on the calendar — there is no override.';

export interface MaintenanceWindowQuery {
  fromMs: number;
  toMs: number;
}

export function useMaintenanceWindows(range: MaintenanceWindowQuery | null) {
  const siteId = useSafeSiteId();
  return useQuery<MaintenanceWindow[]>({
    queryKey: maintenanceKey(siteId, range?.fromMs ?? 0, range?.toMs ?? 0),
    queryFn: () =>
      fetchMaintenanceWindows(siteId as SiteId, range!.fromMs, range!.toMs),
    enabled: Boolean(siteId) && range !== null,
    // Shorter than the schedule window's 15s. A window is the reason
    // dispatch is being refused, so a stale answer is one an operator
    // acts on: they delete a window and expect to dispatch immediately.
    staleTime: 5_000,
  });
}

/** Every cached window range for this site. A write changes what any
 *  of them contains, so they all go — the same reason the schedule
 *  hooks invalidate their whole prefix. */
function invalidateWindows(qc: ReturnType<typeof useQueryClient>, siteId: string) {
  void qc.invalidateQueries({ queryKey: [MAINTENANCE_KEY, siteId] });
  // The capability answer does not change, but a task the operator was
  // about to dispatch may now be refused (or newly allowed), and the
  // tasks list is where they will look.
  void qc.invalidateQueries({ queryKey: ['schedule-occurrences', siteId] });
  // A run inside blocked time does not count as planned, so the Month
  // view's plan check changes with the windows.
  void invalidatePlanCheck(qc, siteId);
}

export function useCreateMaintenanceWindow() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<MaintenanceWindow, Error, CreateMaintenanceWindowRequest>({
    mutationFn: (body) => createMaintenanceWindow(siteId as SiteId, body),
    onSuccess: () => invalidateWindows(qc, siteId),
  });
}

export function useDeleteMaintenanceWindow() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<void, Error, string>({
    mutationFn: (windowId) => deleteMaintenanceWindow(siteId as SiteId, windowId),
    onSuccess: () => invalidateWindows(qc, siteId),
  });
}

/** The window covering `atMs`, or undefined.
 *
 *  Half-open `[start, end)`, matching the server's interval test
 *  exactly — an off-by-one here would let the form promise a dispatch
 *  the command path then refuses, which is worse than not checking. */
export function windowCovering(
  windows: readonly MaintenanceWindow[] | undefined,
  atMs: number,
): MaintenanceWindow | undefined {
  return (windows ?? []).find(
    (w) => w.starts_at_ms <= atMs && atMs < w.ends_at_ms,
  );
}
